import FormFooter from "@/components/form-footer";
import { LAYOUT_OPTIONS } from "@/config/enums";
import { useLayout } from "@/layouts/use-layout";
import routes from "@/routers/routes";
import { updateService, createService } from "@/server/api-services";
import { Service } from "@/types";
import { ServiceFormSchema } from "@/validators/create-service-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { cn } from "rizzui";
import FormNav, { FormParts } from "./form-nav";
import FormServices from "./form-services";
import { defaultValues } from "./form-utils";


const MAP_STEP_TO_COMPONENT = {
    [FormParts.ServiceInfo]: FormServices,
};

interface IndexProps {
    id?: string;
    className?: string;
    setting?: Service;
    isEdit?: boolean;
}

export default function CreateEditService({
    id,
    setting,
    className,
    isEdit,
}: IndexProps) {
    const navigate = useNavigate();
    const { layout } = useLayout();
    const [isLoading, setLoading] = useState(false);

    const methods = useForm<Service>({
        defaultValues: setting ? setting : defaultValues(setting),
        resolver: zodResolver(ServiceFormSchema),
    });

    useEffect(() => {
        if (isEdit && setting) {
            methods.reset(
                defaultValues({
                    ...setting,
                }),
            );
        }
    }, [isEdit, setting]);

    const onSubmit: SubmitHandler<Service> = async (data) => {
        setLoading(true);
        try {
            if (isEdit) {
                await updateService(data.id ?? "", data);
            } else {
                await createService(data);
            }
            navigate(routes.settings.services.list);
        } catch (error) {
            toast.error("Failed to create service");
            console.error("Failed to create service", error);
        } finally {
            setLoading(false);
        }
    };

    console.log("errors", methods.formState?.errors);

    return (
        <div className="@container">
            <FormNav
                className={cn(
                    layout === LAYOUT_OPTIONS.BERYLLIUM && "2xl:top-[72px]",
                )}
            />
            <FormProvider {...methods}>
                <form
                    className={cn("mt-6", className)}
                    onSubmit={methods.handleSubmit(onSubmit)}
                >
                    <div className="mb-10 grid gap-7 divide-y divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
                        {Object.entries(MAP_STEP_TO_COMPONENT).map(
                            ([key, Component]) => (
                                <div key={key}>
                                    <Component className="pt-7 @2xl:pt-9 @3xl:pt-11" />
                                </div>
                            ),
                        )}
                    </div>
                    <FormFooter
                        isLoading={isLoading}
                        handleAltBtn={() => navigate(-1)}
                        altBtnText="Cancel"
                        submitBtnText={id ? "Update Service" : "Create Service"}
                    />                    
                </form>
            </FormProvider>
        </div>
    );
}
